import React from 'react';
import { Box, Typography, Button, Paper, Chip } from '@mui/material';
import {
  Toll as CreditIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import type { PaymentProduct } from '../../api/checkout';
import { buildAuthUrl, creditPackFeatures, formatPrice, calculatePerBoardPrice } from '../../utils/pricing';

export interface CreditPackCardProps {
  product: PaymentProduct;
  isBestValue: boolean;
  isAuthenticated: boolean;
  onNavigate: (path: string) => void;
  onTrack: (buttonName: string, destination: string) => void;
}

export const CreditPackCard: React.FC<CreditPackCardProps> = ({ product, isBestValue: bestValue, isAuthenticated, onNavigate, onTrack }) => {
  const credits = product.metadata?.boardCredits || 0;
  const price = product.prices?.[0];
  const destination = isAuthenticated ? '/credits?buy=true' : buildAuthUrl('/register', '/credits?buy=true');

  const handleClick = () => {
    onTrack(`buy_credits_${credits}`, destination);
    onNavigate(destination);
  };

  return (
    <Paper
      sx={{
        p: 4,
        textAlign: 'center',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 2,
        borderColor: bestValue ? 'primary.main' : 'divider',
        position: 'relative',
      }}
    >
      {bestValue && (
        <Chip
          label="BEST VALUE"
          color="primary"
          size="small"
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: bestValue ? 2 : 0 }}>
        <CreditIcon color="primary" />
        <Typography variant="h3" color="primary">
          {credits}
        </Typography>
      </Box>
      <Typography variant="h6" gutterBottom>
        {product.name}
      </Typography>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {price ? formatPrice(price.amount) : 'N/A'}
      </Typography>
      {price && credits > 0 && (
        <Typography variant="body2" color="success.main" sx={{ mb: 3 }}>
          {calculatePerBoardPrice(price.amount, credits)}
        </Typography>
      )}
      <Box sx={{ textAlign: 'left', mb: 3, flexGrow: 1 }}>
        {creditPackFeatures.map((feature, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CheckIcon color="success" fontSize="small" />
            <Typography variant="body2">{feature}</Typography>
          </Box>
        ))}
      </Box>
      <Button
        variant={bestValue ? 'contained' : 'outlined'}
        fullWidth
        onClick={handleClick}
      >
        {isAuthenticated ? 'Buy Credits' : 'Get Started'}
      </Button>
    </Paper>
  );
};
